import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, SecretTextField, TextField } from "@stacklet/ui";
import { confirmResetPassword } from "aws-amplify/auth";
import { Controller, useForm } from "react-hook-form";

import { AuthScreenLayout } from "./AuthScreenLayout";
import { emailedResetSchema, type EmailedResetValues } from "./schema";
import "./Login.css";

/**
 * Path served by this page. Password reset emails link here (with the
 * verification code as a query param), so treat both the path and the param
 * name as a public contract: changing either breaks the link in every email
 * already sent.
 */
export const RESET_PASSWORD_PATH = "/reset-password";

/**
 * Query param carrying the emailed verification code. Deliberately NOT
 * `code`: Amplify reads a `code` param as an OAuth callback (v5 on any load
 * with oauth configured, v6 while a redirect it started is in flight), and a
 * link in an email can't know what the browser is in the middle of.
 */
export const RESET_CODE_PARAM = "reset-code";

/** Explains where the code comes from, and what to do with an expired one. */
const CODE_HINT =
  "The code from your password reset email. If it has expired, request a new one with “Reset Password” on the sign-in page.";

/**
 * Standalone page where a user completes a password reset: they arrive from
 * the reset email (which pre-fills the verification code via `?reset-code=`)
 * and confirm the reset with their email address and a new password.
 *
 * This lives outside the Amplify Authenticator because its state machine
 * can't be deep-linked into the confirm-reset step without first sending a
 * fresh code, which would orphan the code the user was just emailed —
 * admin-initiated (resetUserPassword) codes included. Cognito accepts any
 * outstanding reset code here via confirmResetPassword.
 */
export function ResetPasswordPage() {
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { control, handleSubmit, formState } = useForm<EmailedResetValues>({
    resolver: zodResolver(emailedResetSchema),
    defaultValues: {
      username: "",
      code:
        new URLSearchParams(window.location.search).get(RESET_CODE_PARAM) ?? "",
      password: "",
      confirmPassword: "",
    },
  });

  const onValid = async ({ username, code, password }: EmailedResetValues) => {
    setError(null);
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword: password,
      });
      setDone(true);
    } catch (err) {
      // Cognito's messages here are user-appropriate (wrong code, expired
      // code, password policy); show them rather than a generic failure.
      setError(
        err instanceof Error ? err.message : "Unable to reset the password",
      );
    }
  };

  if (done) {
    return (
      <AuthScreenLayout
        subtitle="Your password has been reset. You can now sign in with it."
        title="All set"
      >
        <Button
          className="flex items-center justify-center"
          onPress={() => window.location.assign(window.location.origin)}
          variant="primary"
        >
          Go to sign in
        </Button>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout
      subtitle="Enter the code we emailed you and choose a new password."
      title="Choose a new password"
    >
      <form
        className="flex flex-col gap-lg"
        onSubmit={handleSubmit(onValid)}
        noValidate
      >
        <Controller
          control={control}
          name="username"
          render={({ field, fieldState }) => (
            <TextField
              assistiveText={fieldState.error?.message}
              inputProps={{
                autoComplete: "username",
                className: "bg-background-primary",
                placeholder: "you@company.com or username",
              }}
              isInvalid={fieldState.invalid}
              label="Email or username"
              type="text"
              isRequired
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="code"
          render={({ field, fieldState }) => (
            <TextField
              assistiveText={fieldState.error?.message ?? CODE_HINT}
              inputProps={{
                autoComplete: "one-time-code",
                className: "bg-background-primary",
                placeholder: "123456",
              }}
              isInvalid={fieldState.invalid}
              label="Verification code"
              isRequired
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field, fieldState }) => (
            <SecretTextField
              assistiveText={fieldState.error?.message}
              inputProps={{
                autoComplete: "new-password",
                className: "bg-background-primary",
              }}
              isInvalid={fieldState.invalid}
              label="New password"
              isRequired
              {...field}
            />
          )}
          rules={{ deps: ["confirmPassword"] }}
        />
        <Controller
          control={control}
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <SecretTextField
              assistiveText={fieldState.error?.message}
              inputProps={{
                autoComplete: "new-password",
                className: "bg-background-primary",
              }}
              isInvalid={fieldState.invalid}
              label="Confirm password"
              isRequired
              {...field}
            />
          )}
          rules={{ deps: ["password"] }}
        />

        {error ? (
          <p
            className="text-label-medium text-foreground-negative"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <Button
          className="flex items-center justify-center"
          data-testid="reset-password-page-submit"
          isPending={formState.isSubmitting}
          type="submit"
          variant="primary"
        >
          Reset password
        </Button>
      </form>
    </AuthScreenLayout>
  );
}
